// Overlap matrix — rows are carriers, columns are one kind of resource.
// Identical rows mean identical loadouts; a column with one cell is an orphan.
// Rows (within each domain) and columns are ordered by similarity so clusters
// surface on their own. Repositories only carry skills, so they appear on the
// Skills tab alone.

import * as React from "react";

import {
  type Graph,
  type MapNode,
  type ResourceKind,
  neighboursOf,
  nodesOf,
  orderBySimilarity,
} from "./model";
import { sourceColor } from "./wiring";

const TABS: { kind: ResourceKind; label: string }[] = [
  { kind: "skill", label: "Skills" },
  { kind: "tool", label: "Tools" },
  { kind: "kb", label: "Knowledge" },
  { kind: "model", label: "Models" },
];

const DRIFT_FILL =
  "repeating-linear-gradient(135deg, var(--map-drift) 0 3px, var(--map-drift-soft) 3px 6px)";

export function OverlapMatrix({ graph }: { graph: Graph }) {
  const [kind, setKind] = React.useState<ResourceKind>("skill");

  const { groups, columns } = React.useMemo(() => {
    const setOf = (n: MapNode) => neighboursOf(graph, n.id, kind).map((x) => x.id);
    const groups: { label: string; rows: MapNode[] }[] = graph.data.domains
      .map((d) => ({
        label: d.name,
        rows: orderBySimilarity(
          nodesOf(graph, "agent").filter((a) => a.domainId === d.id),
          setOf,
        ),
      }))
      .filter((g) => g.rows.length);
    if (kind === "skill" && graph.data.repos.length) {
      groups.push({
        label: "GitHub repositories",
        rows: orderBySimilarity(nodesOf(graph, "repo"), setOf),
      });
    }
    const columns = orderBySimilarity(nodesOf(graph, kind), (n) =>
      neighboursOf(graph, n.id).map((x) => x.id),
    );
    return { groups, columns };
  }, [graph, kind]);

  return (
    <div className="space-y-3">
      <div
        className="inline-flex overflow-hidden rounded border"
        role="group"
        aria-label="Matrix columns"
      >
        {TABS.map((t) => (
          <button
            key={t.kind}
            type="button"
            aria-pressed={kind === t.kind}
            onClick={() => setKind(t.kind)}
            className={
              kind === t.kind
                ? "bg-foreground px-3 py-1 text-xs text-background"
                : "px-3 py-1 text-xs text-muted-foreground hover:text-foreground"
            }
          >
            {t.label}
          </button>
        ))}
      </div>
      {columns.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          Nothing of this kind in the organisation yet.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="border-separate text-xs" style={{ borderSpacing: 2 }}>
            <thead>
              <tr>
                <th />
                {columns.map((c) => (
                  <th key={c.id} scope="col" className="relative h-32 w-[22px] p-0 align-bottom">
                    <span
                      className="block w-[22px] origin-bottom-left whitespace-nowrap text-[11px] font-semibold text-muted-foreground"
                      style={{ transform: "rotate(-62deg) translate(6px, 10px)" }}
                    >
                      {c.name}
                    </span>
                  </th>
                ))}
                <th />
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => (
                <React.Fragment key={group.label}>
                  <tr>
                    <th
                      scope="rowgroup"
                      colSpan={columns.length + 2}
                      className="pt-3 text-left text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
                    >
                      {group.label}
                    </th>
                  </tr>
                  {group.rows.map((row) => {
                    const mine = new Map(
                      neighboursOf(graph, row.id, kind).map((x) => [x.id, x.variant]),
                    );
                    return (
                      <tr key={row.id}>
                        <th
                          scope="row"
                          className="whitespace-nowrap pr-2 text-left font-semibold text-foreground/80"
                        >
                          {row.name}
                        </th>
                        {columns.map((c) => {
                          const held = mine.has(c.id);
                          const differs = mine.get(c.id);
                          return (
                            <td
                              key={c.id}
                              className="h-[22px] w-[22px] rounded-sm bg-muted p-0"
                              style={
                                held
                                  ? { background: differs ? DRIFT_FILL : sourceColor(c.source) }
                                  : undefined
                              }
                              title={
                                held
                                  ? `${row.name} → ${c.name}${differs ? " (content differs from canonical)" : ""}`
                                  : undefined
                              }
                            />
                          );
                        })}
                        <td className="pl-2 text-right tabular-nums text-muted-foreground">
                          {mine.size}
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th className="pr-2 text-right text-[10px] font-normal text-muted-foreground">
                  carriers
                </th>
                {columns.map((c) => (
                  <th
                    key={c.id}
                    className="pt-1 text-center text-[10px] font-normal tabular-nums text-muted-foreground"
                  >
                    {
                      neighboursOf(graph, c.id).filter((x) =>
                        kind === "skill" ? true : graph.nodes.get(x.id)?.kind === "agent",
                      ).length
                    }
                  </th>
                ))}
                <th />
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}
